import { Badge, Button, Card, MUTED_TEXT, Spinner, stackStyle } from "@dayvise/react-ui";
import { useWebPushRegistration } from "./webPush.ts";

const SERVICE_WORKER_URL = "/service-worker.js";

/**
 * Browser push settings for the web client. The enable control IS the user
 * action that triggers the permission prompt (CLIENT-03); a denied permission,
 * an unsupported browser, or a deployment without web-push keys is stated here
 * rather than promised (CLIENT-04). Push delivers a data-only summary; the app
 * still re-checks the authenticated inbox before showing anything private.
 */
export function WebPushSettingsCard() {
  const { state, enable, disable } = useWebPushRegistration(SERVICE_WORKER_URL);
  const current = state.data ?? null;
  const outcome = (enable.submittedAt > disable.submittedAt ? enable.data : disable.data) ?? null;
  const busy = enable.isPending || disable.isPending;

  return (
    <Card
      title="Browser push notifications"
      subtitle="Receive Dayvise notifications while this tab is closed. Registration happens only when you click enable; the browser's own permission prompt decides, and text-only in-app notifications keep working in every state."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {state.isPending ? <Spinner label="Checking browser push support" /> : null}
        {state.error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{state.error instanceof Error ? state.error.message : "Could not read this browser's push state"}</p> : null}
        {current?.kind === "registered" ? <Badge tone="success">Push registered</Badge> : null}
        {current?.kind === "unregistered" ? <Badge tone="neutral">Not registered</Badge> : null}
        {current?.kind === "denied" ? <Badge tone="warning">Notifications blocked in this browser</Badge> : null}
        {current?.kind === "unsupported" ? <Badge tone="neutral">Not available in this browser</Badge> : null}
        {current?.kind === "unsupported" ? <p style={{ ...MUTED_TEXT, margin: 0 }}>{current.reason}</p> : null}
        {outcome ? <p style={{ ...MUTED_TEXT, margin: 0 }}>{outcome.message}</p> : null}
        {enable.error ? (
          <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{enable.error instanceof Error ? enable.error.message : "Could not register push"}</p>
        ) : null}
        {disable.error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{disable.error instanceof Error ? disable.error.message : "Could not disable push"}</p> : null}
        <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
          <Button onClick={() => enable.mutate()} disabled={busy || current?.kind === "registered" || current?.kind === "unsupported" || current?.kind === "denied"}>
            {enable.isPending ? "Registering…" : "Enable browser push"}
          </Button>
          <Button onClick={() => disable.mutate()} disabled={busy || current?.kind !== "registered"}>
            {disable.isPending ? "Disabling…" : "Disable"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
